import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Spin } from 'antd'
import { CheckCircleOutlined, DownloadOutlined } from '@ant-design/icons'
import { useComplianceAssets } from '@/features/compliance/hooks'
import { downloadComplianceExport } from '@/features/compliance/api'
import { getComplianceRowStatus } from '@/features/compliance/utils'
import { useToast } from '@/contexts/ToastContext'

type ExportReportStepProps = {
  clientId: string
  jobId: string
  orgId: string
  onBack?: () => void
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

export function ExportReportStep({ clientId, jobId, orgId, onBack }: ExportReportStepProps) {
  const { addToast } = useToast()
  const [exporting, setExporting] = useState<'excel' | 'pdf' | null>(null)

  const assetsQuery = useComplianceAssets(clientId, jobId)
  const rows = assetsQuery.data ?? []

  const summary = (() => {
    let match = 0
    let mismatch = 0
    let missing = 0
    rows.forEach((r) => {
      const s = getComplianceRowStatus(r)
      if (s === 'match') match++
      else if (s === 'mismatch') mismatch++
      else missing++
    })
    return { total: rows.length, match, mismatch, missing }
  })()

  const handleExport = async (format: 'excel' | 'pdf') => {
    setExporting(format)
    try {
      const { blob, filename } = await downloadComplianceExport(clientId, jobId, format)
      saveBlob(blob, filename)
      addToast(`Exported to ${format === 'excel' ? 'Excel' : 'PDF'}`, 'success')
    } catch (e) {
      addToast(e instanceof Error ? e.message : `Failed to export ${format}`, 'error')
    } finally {
      setExporting(null)
    }
  }

  const summaryText =
    summary.total === 0
      ? 'No line items to report.'
      : `${summary.total} line item${summary.total === 1 ? '' : 's'} reviewed. ${summary.match} matched, ${summary.mismatch} mismatch${summary.mismatch === 1 ? '' : 'es'} flagged, ${summary.missing} missing workpaper${summary.missing === 1 ? '' : 's'} identified.`

  if (assetsQuery.isLoading && !assetsQuery.data) {
    return (
      <div className="flex min-h-[200px] items-center justify-center rounded-xl border border-slate-200 bg-slate-50/50">
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center py-8">
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
        <CheckCircleOutlined className="text-4xl text-green-600" />
      </div>
      <h2 className="text-2xl font-bold text-slate-900">Review Complete</h2>
      <p className="mt-2 max-w-md text-center text-slate-600">{summaryText}</p>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <Button
          type="primary"
          size="large"
          icon={<DownloadOutlined />}
          loading={exporting === 'excel'}
          onClick={() => handleExport('excel')}
        >
          Export to Excel
        </Button>
        <Button
          size="large"
          icon={<DownloadOutlined />}
          loading={exporting === 'pdf'}
          onClick={() => handleExport('pdf')}
        >
          Export to PDF
        </Button>
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        {onBack && (
          <Button size="large" onClick={onBack}>
            ← Back to Review Results
          </Button>
        )}
        <Link to={orgId ? `/annual-compliance?org=${encodeURIComponent(orgId)}` : '/annual-compliance'}>
          <Button size="large">Start New Review</Button>
        </Link>
      </div>
    </div>
  )
}
